import { formatDateTime } from '@fruitful/fruitils';
import type { AccountSummary, HolisticSummary } from '@fruitful/types';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputSwitch } from 'primereact/inputswitch';
import React, { useMemo, useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    ChevronDownIcon,
    ChevronUpIcon,
    Icon,
    IconButton,
    LabelRegular,
    MonoBodySmallRegular,
    RefreshCwIcon,
    Spinner,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { TextError } from '../../../common/components/InlineError';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import type { Bill, MembershipDetails } from '../../entityTypes';
import {
    type FruitfulAiMembershipJob,
    getJobAccountId,
    isBillsRelatedFruitfulAiJob,
    useFruitfulAiJobsForMembership,
} from '../hooks/useFruitfulAiJobsForMembership';
import { useFruitfulAiTransactionCountsByConnectionAccount } from '../hooks/useFruitfulAiTransactionCountsByConnectionAccount';
import { useMembershipBillsIncludingInactive } from '../hooks/useMembershipBills';
import { useRefreshBillsForAccountMutation } from '../hooks/useRefreshBillsForAccount';
import { BillDetailModal } from './BillDetailModal';
import { BillEditModal } from './BillEditModal';
import { BillsTable } from './MembershipBillsInfo';

const UNKNOWN_ACCOUNT_KEY = 'UNKNOWN_ACCOUNT';

const flattenHolisticAccounts = (
    summary: HolisticSummary | undefined,
): AccountSummary[] => {
    if (summary == null) {
        return [];
    }
    return [
        ...summary.spend.accounts,
        ...summary.save.accounts,
        ...summary.invest.accounts,
        ...summary.borrow.accounts,
    ];
};

const buildAccountIdLookup = (
    accounts: AccountSummary[],
): Map<string, AccountSummary> => {
    const map = new Map<string, AccountSummary>();
    for (const account of accounts) {
        map.set(account.id, account);
    }
    return map;
};

const accountDisplayTitle = (
    accountId: string,
    lookup: Map<string, AccountSummary>,
): string => {
    if (accountId === UNKNOWN_ACCOUNT_KEY) {
        return 'Unknown account';
    }
    const account = lookup.get(accountId);
    if (account == null) {
        return 'Account not in holistic summary';
    }
    const institution = account.institutionName?.trim() ?? '';
    const name = account.name?.trim() ?? '';
    if (institution !== '' && name !== '') {
        return `${institution} · ${name}`;
    }
    return institution || name || accountId;
};

const jobStringField = (v: unknown): string | undefined =>
    typeof v === 'string' && v !== '' ? v : undefined;

const formatJobIsoMaybe = (v: unknown): string | undefined => {
    const s = jobStringField(v);
    if (s == null) {
        return undefined;
    }
    try {
        return formatDateTime(s);
    } catch {
        return s;
    }
};

const stableJobKey = (job: FruitfulAiMembershipJob, index: number): string =>
    jobStringField(job._id) ??
    jobStringField(job.id) ??
    jobStringField(job.jobId) ??
    `idx-${index}`;

const dash = (value: string | undefined): string =>
    value != null && value !== '' ? value : '—';

interface FruitfulAiJobTableRow {
    rowKey: string;
    name: string;
    type: string;
    jobId: string;
    priority: string;
    lastRun: string;
    lastFinished: string;
    lockedAt: string;
    nextRun: string;
    failedAt: string;
    failCount: string;
    failReason: string;
    progress: string;
    status: string;
    ingestStart: string;
    ingestEnd: string;
    profileId: string;
    connectionAccountId: string;
    localAccountId: string;
    dataMembershipId: string;
    userId: string;
    linkProvider: string;
    aggregator: string;
    initialRunForAccount: string;
    overwrite: string;
}

const formatDataField = (
    data: Record<string, unknown> | undefined,
    key: string,
    asIsoDate: boolean,
): string => {
    if (data == null) {
        return '—';
    }
    const raw = data[key];
    if (raw == null || raw === '') {
        return '—';
    }
    if (typeof raw === 'boolean') {
        return raw ? 'Yes' : 'No';
    }
    if (
        asIsoDate &&
        typeof raw === 'string' &&
        /^\d{4}-\d{2}-\d{2}T/.test(raw)
    ) {
        return dash(formatJobIsoMaybe(raw));
    }
    if (typeof raw === 'string') {
        return raw;
    }
    return String(raw);
};

const jobToTableRow = (
    job: FruitfulAiMembershipJob,
    index: number,
): FruitfulAiJobTableRow => {
    const data =
        typeof job.data === 'object' && job.data != null
            ? (job.data as Record<string, unknown>)
            : undefined;

    const jobId =
        jobStringField(job._id) ??
        jobStringField(job.id) ??
        jobStringField(job.jobId);

    let progressText = '—';
    if (job.progress != null) {
        progressText =
            typeof job.progress === 'string'
                ? job.progress
                : JSON.stringify(job.progress);
    }

    return {
        rowKey: stableJobKey(job, index),
        name: dash(jobStringField(job.name)),
        type: dash(jobStringField(job.type) ?? jobStringField(job.jobType)),
        jobId: dash(jobId),
        priority: typeof job.priority === 'number' ? String(job.priority) : '—',
        lastRun: dash(formatJobIsoMaybe(job.lastRunAt)),
        lastFinished: dash(formatJobIsoMaybe(job.lastFinishedAt)),
        lockedAt: dash(formatJobIsoMaybe(job.lockedAt)),
        nextRun: dash(formatJobIsoMaybe(job.nextRunAt)),
        failedAt: dash(formatJobIsoMaybe(job.failedAt)),
        failCount:
            typeof job.failCount === 'number' ? String(job.failCount) : '—',
        failReason: dash(jobStringField(job.failReason)),
        progress: progressText === '' ? '—' : progressText,
        status: dash(jobStringField(job.status) ?? jobStringField(job.state)),
        ingestStart: formatDataField(data, 'start', true),
        ingestEnd: formatDataField(data, 'end', true),
        profileId: formatDataField(data, 'profileId', false),
        connectionAccountId: formatDataField(data, 'accountId', false),
        localAccountId: formatDataField(data, 'localAccountId', false),
        dataMembershipId: formatDataField(data, 'membershipId', false),
        userId: formatDataField(data, 'userId', false),
        linkProvider: formatDataField(data, 'linkProvider', false),
        aggregator: formatDataField(data, 'aggregator', false),
        initialRunForAccount: formatDataField(
            data,
            'isInitialRunForAccount',
            false,
        ),
        overwrite: formatDataField(data, 'overwrite', false),
    };
};

const FruitfulAiJobTableMonoCell = ({ text }: { text: string }) => (
    <MonoBodySmallRegular color="$gray1200" selectable>
        {text}
    </MonoBodySmallRegular>
);

const FruitfulAiJobsDataTable = ({
    jobs,
}: {
    jobs: FruitfulAiMembershipJob[];
}) => {
    const rows = useMemo(
        () => jobs.map((job, index) => jobToTableRow(job, index)),
        [jobs],
    );

    return (
        <DataTable
            dataKey="rowKey"
            value={rows}
            size="small"
            scrollable
            scrollHeight="28rem"
            sortField="lastRun"
            sortOrder={-1}
            tableStyle={{ minWidth: '1600px' }}
        >
            <Column
                field="name"
                header="Name"
                sortable
                style={{ minWidth: '11rem' }}
            />
            <Column
                field="type"
                header="Type"
                sortable
                style={{ minWidth: '6rem' }}
            />
            <Column
                field="jobId"
                header="Job ID"
                sortable
                style={{ minWidth: '12rem' }}
                body={(row: FruitfulAiJobTableRow) => (
                    <FruitfulAiJobTableMonoCell text={row.jobId} />
                )}
            />
            <Column
                field="priority"
                header="Priority"
                sortable
                style={{ minWidth: '5rem' }}
            />
            <Column
                field="lastRun"
                header="Last run"
                sortable
                style={{ minWidth: '10rem' }}
            />
            <Column
                field="lastFinished"
                header="Last finished"
                sortable
                style={{ minWidth: '10rem' }}
            />
            <Column
                field="lockedAt"
                header="Locked at"
                sortable
                style={{ minWidth: '10rem' }}
            />
            <Column
                field="nextRun"
                header="Next run"
                sortable
                style={{ minWidth: '10rem' }}
            />
            <Column
                field="failedAt"
                header="Failed at"
                sortable
                style={{ minWidth: '10rem' }}
            />
            <Column
                field="failCount"
                header="Fail count"
                sortable
                style={{ minWidth: '6rem' }}
            />
            <Column
                field="failReason"
                header="Fail reason"
                sortable
                style={{ minWidth: '12rem' }}
            />
            <Column
                field="progress"
                header="Progress"
                sortable
                style={{ minWidth: '10rem' }}
                body={(row: FruitfulAiJobTableRow) => (
                    <FruitfulAiJobTableMonoCell text={row.progress} />
                )}
            />
            <Column
                field="status"
                header="Status"
                sortable
                style={{ minWidth: '7rem' }}
            />
            <Column
                field="ingestStart"
                header="Ingest start"
                sortable
                style={{ minWidth: '10rem' }}
            />
            <Column
                field="ingestEnd"
                header="Ingest end"
                sortable
                style={{ minWidth: '10rem' }}
            />
            <Column
                field="profileId"
                header="Profile ID"
                sortable
                style={{ minWidth: '10rem' }}
                body={(row: FruitfulAiJobTableRow) => (
                    <FruitfulAiJobTableMonoCell text={row.profileId} />
                )}
            />
            <Column
                field="connectionAccountId"
                header="Connection account"
                sortable
                style={{ minWidth: '12rem' }}
                body={(row: FruitfulAiJobTableRow) => (
                    <FruitfulAiJobTableMonoCell
                        text={row.connectionAccountId}
                    />
                )}
            />
            <Column
                field="localAccountId"
                header="Local account ID"
                sortable
                style={{ minWidth: '12rem' }}
                body={(row: FruitfulAiJobTableRow) => (
                    <FruitfulAiJobTableMonoCell text={row.localAccountId} />
                )}
            />
            <Column
                field="dataMembershipId"
                header="Membership (data)"
                sortable
                style={{ minWidth: '10rem' }}
                body={(row: FruitfulAiJobTableRow) => (
                    <FruitfulAiJobTableMonoCell text={row.dataMembershipId} />
                )}
            />
            <Column
                field="userId"
                header="User ID"
                sortable
                style={{ minWidth: '10rem' }}
                body={(row: FruitfulAiJobTableRow) => (
                    <FruitfulAiJobTableMonoCell text={row.userId} />
                )}
            />
            <Column
                field="linkProvider"
                header="Link provider"
                sortable
                style={{ minWidth: '8rem' }}
            />
            <Column
                field="aggregator"
                header="Aggregator"
                sortable
                style={{ minWidth: '8rem' }}
            />
            <Column
                field="initialRunForAccount"
                header="Initial run"
                sortable
                style={{ minWidth: '7rem' }}
            />
            <Column
                field="overwrite"
                header="Overwrite"
                sortable
                style={{ minWidth: '6rem' }}
            />
        </DataTable>
    );
};

const FruitfulAiJobsCollapsibleSection = ({
    title,
    jobs,
    isLoading,
    isError,
    emptyCountSummary,
    emptyExpandedMessage,
}: {
    title: string;
    jobs: FruitfulAiMembershipJob[];
    isLoading: boolean;
    isError: boolean;
    emptyCountSummary: string;
    emptyExpandedMessage: string;
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const count = jobs.length;
    const statusLine = (() => {
        if (isLoading) {
            return 'Loading jobs…';
        }
        if (isError) {
            return 'Could not load jobs for this membership.';
        }
        if (count === 0) {
            return emptyCountSummary;
        }
        if (count === 1) {
            return '1 job';
        }
        return `${count} jobs`;
    })();

    return (
        <YStack
            width="100%"
            alignSelf="stretch"
            borderWidth={1}
            borderColor="$gray200"
            borderRadius={10}
            overflow="hidden"
        >
            <XStack
                paddingHorizontal={12}
                paddingVertical={10}
                alignItems="center"
                justifyContent="space-between"
                gap={12}
                hoverStyle={{ cursor: 'pointer' }}
                onPress={() => {
                    setIsExpanded((open) => !open);
                }}
            >
                <YStack flex={1} gap={4} flexShrink={1}>
                    <LabelRegular>{title}</LabelRegular>
                    <BodySmallRegular color={isError ? '$red900' : '$gray1000'}>
                        {statusLine}
                    </BodySmallRegular>
                </YStack>
                <Icon
                    color="$gray1000"
                    size={18}
                    icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                />
            </XStack>
            {isExpanded && isLoading && (
                <YStack
                    paddingHorizontal={12}
                    paddingBottom={12}
                    paddingTop={4}
                    borderTopWidth={1}
                    borderColor="$gray200"
                >
                    <BodySmallRegular color="$gray1000">
                        Loading jobs…
                    </BodySmallRegular>
                </YStack>
            )}
            {isExpanded && isError && (
                <YStack
                    paddingHorizontal={12}
                    paddingBottom={12}
                    paddingTop={4}
                    borderTopWidth={1}
                    borderColor="$gray200"
                >
                    <BodySmallRegular color="$red900">
                        Could not load jobs for this membership.
                    </BodySmallRegular>
                </YStack>
            )}
            {isExpanded && !isLoading && !isError && count > 0 && (
                <YStack
                    width="100%"
                    paddingHorizontal={8}
                    paddingBottom={8}
                    paddingTop={4}
                    borderTopWidth={1}
                    borderColor="$gray200"
                >
                    <FruitfulAiJobsDataTable jobs={jobs} />
                </YStack>
            )}
            {isExpanded && !isLoading && !isError && count === 0 && (
                <YStack
                    paddingHorizontal={12}
                    paddingBottom={12}
                    paddingTop={4}
                    borderTopWidth={1}
                    borderColor="$gray200"
                >
                    <BodySmallRegular color="$gray1000" fontStyle="italic">
                        {emptyExpandedMessage}
                    </BodySmallRegular>
                </YStack>
            )}
        </YStack>
    );
};

const AccountFruitfulAiJobsCollapsible = ({
    accountId,
    jobsForAccount,
    isLoading,
    isError,
}: {
    accountId: string;
    jobsForAccount: FruitfulAiMembershipJob[];
    isLoading: boolean;
    isError: boolean;
}) => {
    if (accountId === UNKNOWN_ACCOUNT_KEY) {
        return null;
    }

    return (
        <FruitfulAiJobsCollapsibleSection
            title="Fruitful AI jobs (this account)"
            jobs={jobsForAccount}
            isLoading={isLoading}
            isError={isError}
            emptyCountSummary="No jobs for this account"
            emptyExpandedMessage="No jobs matched this account ID in the response."
        />
    );
};

const AccountHeadingBlock = ({
    accountId,
    account,
    billCount,
    accountById,
    pendingRefreshAccountId,
    onRefreshBillsForAccount,
    aiTrxCount,
}: {
    accountId: string;
    account: AccountSummary | undefined;
    billCount: number;
    accountById: Map<string, AccountSummary>;
    pendingRefreshAccountId: string | null;
    onRefreshBillsForAccount: (accountId: string) => void;
    aiTrxCount: {
        isLoading: boolean;
        isError: boolean;
        count: number | undefined;
    };
}) => {
    const title = accountDisplayTitle(accountId, accountById);
    const canRequestRefresh = accountId !== UNKNOWN_ACCOUNT_KEY;
    const refreshInFlight = pendingRefreshAccountId === accountId;

    return (
        <YStack gap={4}>
            <XStack alignItems="center" gap={6} flexWrap="wrap">
                <BodyMediumEmphasized flexShrink={1}>
                    {title} — {billCount} {billCount === 1 ? 'bill' : 'bills'}
                </BodyMediumEmphasized>
                {canRequestRefresh && (
                    <IconButton
                        variant="tertiary"
                        size="tiny"
                        icon={RefreshCwIcon}
                        disabled={refreshInFlight}
                        onPress={() => {
                            onRefreshBillsForAccount(accountId);
                        }}
                    />
                )}
            </XStack>
            <BodySmallRegular color="$gray1000" selectable>
                Account ID:{' '}
                {accountId === UNKNOWN_ACCOUNT_KEY ? '—' : accountId}
            </BodySmallRegular>
            <BodySmallRegular color="$gray1000">
                Source: {account != null ? account.provider : '—'}
            </BodySmallRegular>
            {account?.accountNumberMasked != null &&
                account.accountNumberMasked !== '' && (
                    <BodySmallRegular color="$gray1000">
                        Mask: {account.accountNumberMasked}
                    </BodySmallRegular>
                )}
            {accountId !== UNKNOWN_ACCOUNT_KEY && (
                <BodySmallRegular color="$gray1000">
                    Transaction Count:{' '}
                    {aiTrxCount.isLoading
                        ? 'Loading…'
                        : aiTrxCount.isError
                          ? 'Could not load'
                          : aiTrxCount.count !== undefined
                            ? String(aiTrxCount.count)
                            : '—'}
                </BodySmallRegular>
            )}
        </YStack>
    );
};

/** Shown only when `authorization.betaTransactionFeatures.canUse` (awareness engineers). */
export const BillsDevToolsTabPanel = ({
    membershipDetails,
    isTabActive = true,
}: {
    membershipDetails: MembershipDetails;
    /** When false, Fruitful AI jobs are not fetched (e.g. another tab is selected). */
    isTabActive?: boolean;
}) => {
    const [showMonthlyAmount, setShowMonthlyAmount] = useState(false);
    const [editingBill, setEditingBill] = useState<Bill | null>(null);
    const [detailBill, setDetailBill] = useState<Bill | null>(null);
    const [pendingRefreshAccountId, setPendingRefreshAccountId] = useState<
        string | null
    >(null);
    const toast = useToastController();
    const refreshBillsForAccountMutation = useRefreshBillsForAccountMutation();

    const { membershipBills, isLoadingBills, isBillsError } =
        useMembershipBillsIncludingInactive(membershipDetails.id);

    const {
        jobs: fruitfulAiJobs,
        isLoadingJobs,
        isJobsError,
    } = useFruitfulAiJobsForMembership(membershipDetails.id, {
        includeFinished: true,
        enabled: isTabActive,
    });

    const {
        trxCountByConnectionAccountId,
        isLoadingTrxCounts,
        isTrxCountsError,
    } = useFruitfulAiTransactionCountsByConnectionAccount(
        membershipDetails.id,
        { enabled: isTabActive },
    );

    const aiTrxCountPropsForAccount = (accountId: string) => ({
        isLoading: isLoadingTrxCounts,
        isError: isTrxCountsError,
        count:
            accountId === UNKNOWN_ACCOUNT_KEY
                ? undefined
                : trxCountByConnectionAccountId[accountId],
    });

    const jobsByAccountId = useMemo(() => {
        const map = new Map<string, FruitfulAiMembershipJob[]>();
        for (const job of fruitfulAiJobs) {
            const aid = getJobAccountId(job);
            if (aid == null) {
                continue;
            }
            const list = map.get(aid) ?? [];
            list.push(job);
            map.set(aid, list);
        }
        return map;
    }, [fruitfulAiJobs]);

    const membershipBillsJobs = useMemo(() => {
        const list = fruitfulAiJobs.filter(isBillsRelatedFruitfulAiJob);
        return [...list].sort((a, b) => {
            const sa = typeof a.lastRunAt === 'string' ? a.lastRunAt : '';
            const sb = typeof b.lastRunAt === 'string' ? b.lastRunAt : '';
            return sb.localeCompare(sa);
        });
    }, [fruitfulAiJobs]);

    const requestRefreshBillsForAccount = (accountId: string) => {
        if (accountId === UNKNOWN_ACCOUNT_KEY) {
            return;
        }
        setPendingRefreshAccountId(accountId);
        refreshBillsForAccountMutation.mutate(
            {
                urlParams: {
                    membershipId: membershipDetails.id,
                    accountId,
                },
            },
            {
                onSuccess: () => {
                    toast.show(
                        'Bill refresh job started.',
                        MutationSuccessToastOptions,
                    );
                },
                onError: (error) => {
                    toast.show(
                        'Could not start bill refresh',
                        MutationErrorToastOptions(
                            error instanceof Error ? error.message : undefined,
                        ),
                    );
                },
                onSettled: () => {
                    setPendingRefreshAccountId(null);
                },
            },
        );
    };

    const holisticAccounts = useMemo(
        () => flattenHolisticAccounts(membershipDetails.holisticSummary),
        [membershipDetails.holisticSummary],
    );

    const accountById = useMemo(
        () => buildAccountIdLookup(holisticAccounts),
        [holisticAccounts],
    );

    const billsWithoutTransactions = useMemo(
        () =>
            (membershipBills.bills ?? []).map((bill) => ({
                ...bill,
                transactions: [],
            })),
        [membershipBills.bills],
    );

    const billsGroupedByAccountId = useMemo(() => {
        const map = new Map<string, Bill[]>();
        for (const bill of billsWithoutTransactions) {
            const key =
                bill.accountId != null && bill.accountId !== ''
                    ? bill.accountId
                    : UNKNOWN_ACCOUNT_KEY;
            const list = map.get(key) ?? [];
            list.push(bill);
            map.set(key, list);
        }
        return map;
    }, [billsWithoutTransactions]);

    const sortedAccountIds = useMemo(() => {
        return [...billsGroupedByAccountId.keys()].sort((a, b) =>
            accountDisplayTitle(a, accountById).localeCompare(
                accountDisplayTitle(b, accountById),
                undefined,
                { sensitivity: 'base' },
            ),
        );
    }, [billsGroupedByAccountId, accountById]);

    const holisticAccountsWithNoBills = useMemo(() => {
        const idsWithBills = new Set(billsGroupedByAccountId.keys());
        return holisticAccounts
            .filter((a) => !idsWithBills.has(a.id))
            .sort((a, b) =>
                accountDisplayTitle(a.id, accountById).localeCompare(
                    accountDisplayTitle(b.id, accountById),
                    undefined,
                    { sensitivity: 'base' },
                ),
            );
    }, [holisticAccounts, billsGroupedByAccountId, accountById]);

    const hasAnyBills = billsWithoutTransactions.length > 0;

    const openBillDetail = (billRow: Bill) => {
        const fullBill =
            membershipBills.bills?.find((b) => b.id === billRow.id) ?? billRow;
        setDetailBill(fullBill);
    };

    if (isBillsError) {
        return <TextError message="Error loading bills data" />;
    }

    if (isLoadingBills) {
        return (
            <XStack alignItems="center" gap={8}>
                <View>
                    <Spinner />
                </View>
                <BodyMediumRegular fontStyle="italic">
                    Loading bills data...
                </BodyMediumRegular>
            </XStack>
        );
    }

    return (
        <YStack gap={16} width="100%" alignSelf="stretch">
            <FruitfulAiJobsCollapsibleSection
                title="Fruitful AI bills jobs (membership)"
                jobs={membershipBillsJobs}
                isLoading={isLoadingJobs}
                isError={isJobsError}
                emptyCountSummary="No bills jobs for this membership"
                emptyExpandedMessage="No bills-related jobs in the response (matched when name, job type, or payload job name/kind contains “bill”)."
            />
            {billsWithoutTransactions.length > 0 && (
                <XStack justifyContent="flex-end" alignItems="center" gap={8}>
                    <BodySmallRegular>Show monthly amounts</BodySmallRegular>
                    <InputSwitch
                        checked={showMonthlyAmount}
                        onChange={(e) => {
                            setShowMonthlyAmount(e.value);
                        }}
                    />
                </XStack>
            )}
            {!hasAnyBills &&
                holisticAccountsWithNoBills.length === 0 &&
                holisticAccounts.length === 0 && (
                    <BodyMediumRegular fontStyle="italic">
                        No bills for this membership and no holistic accounts
                        loaded.
                    </BodyMediumRegular>
                )}
            {!hasAnyBills &&
                holisticAccountsWithNoBills.length === 0 &&
                holisticAccounts.length > 0 && (
                    <BodyMediumRegular fontStyle="italic" color="$gray1000">
                        No bills for this membership.
                    </BodyMediumRegular>
                )}
            {hasAnyBills && (
                <YStack gap={24} width="100%" alignSelf="stretch">
                    {sortedAccountIds.map((accountId) => {
                        const billsInGroup =
                            billsGroupedByAccountId.get(accountId) ?? [];
                        if (billsInGroup.length === 0) {
                            return null;
                        }
                        const account = accountById.get(accountId);
                        return (
                            <YStack
                                key={accountId}
                                gap={8}
                                width="100%"
                                alignSelf="stretch"
                            >
                                <AccountHeadingBlock
                                    accountId={accountId}
                                    account={account}
                                    billCount={billsInGroup.length}
                                    accountById={accountById}
                                    pendingRefreshAccountId={
                                        pendingRefreshAccountId
                                    }
                                    onRefreshBillsForAccount={
                                        requestRefreshBillsForAccount
                                    }
                                    aiTrxCount={aiTrxCountPropsForAccount(
                                        accountId,
                                    )}
                                />
                                <AccountFruitfulAiJobsCollapsible
                                    accountId={accountId}
                                    jobsForAccount={
                                        jobsByAccountId.get(accountId) ?? []
                                    }
                                    isLoading={isLoadingJobs}
                                    isError={isJobsError}
                                />
                                <BillsTable
                                    membershipDetails={membershipDetails}
                                    bills={billsInGroup}
                                    showMonthlyAmount={showMonthlyAmount}
                                    setEditingBill={setEditingBill}
                                    showRowIndex
                                    onBillRowClick={openBillDetail}
                                />
                            </YStack>
                        );
                    })}
                </YStack>
            )}
            {holisticAccountsWithNoBills.length > 0 && (
                <YStack
                    gap={16}
                    width="100%"
                    alignSelf="stretch"
                    paddingTop={hasAnyBills ? 16 : 0}
                    borderTopWidth={hasAnyBills ? 1 : 0}
                    borderColor="$gray200"
                >
                    <BodyMediumEmphasized>
                        Holistic accounts with no bills
                    </BodyMediumEmphasized>
                    <BodySmallRegular color="$gray1000">
                        In holistic summary but no bill uses this account ID.
                    </BodySmallRegular>
                    <YStack gap={24}>
                        {holisticAccountsWithNoBills.map((account) => (
                            <YStack
                                key={account.id}
                                gap={8}
                                width="100%"
                                alignSelf="stretch"
                            >
                                <AccountHeadingBlock
                                    accountId={account.id}
                                    account={account}
                                    billCount={0}
                                    accountById={accountById}
                                    pendingRefreshAccountId={
                                        pendingRefreshAccountId
                                    }
                                    onRefreshBillsForAccount={
                                        requestRefreshBillsForAccount
                                    }
                                    aiTrxCount={aiTrxCountPropsForAccount(
                                        account.id,
                                    )}
                                />
                                <AccountFruitfulAiJobsCollapsible
                                    accountId={account.id}
                                    jobsForAccount={
                                        jobsByAccountId.get(account.id) ?? []
                                    }
                                    isLoading={isLoadingJobs}
                                    isError={isJobsError}
                                />
                                <BodyMediumRegular
                                    fontStyle="italic"
                                    color="$gray1000"
                                >
                                    No bills linked to this account.
                                </BodyMediumRegular>
                            </YStack>
                        ))}
                    </YStack>
                </YStack>
            )}
            <BillDetailModal
                bill={detailBill}
                isOpen={detailBill != null}
                onClose={() => {
                    setDetailBill(null);
                }}
            />
            {editingBill != null && (
                <BillEditModal
                    bill={editingBill}
                    isOpen={true}
                    onClose={() => {
                        setEditingBill(null);
                    }}
                />
            )}
        </YStack>
    );
};
